// ScreenMetaContext — runtime registry of "what's on screen" so the
// AI's `inspect_current_page` tool can return Layer C-rich metadata.
//
// Spec: docs/specs/ai-chat-guided-tour.md §5.2 (Layer C-rich) +
// §10.2 (`inspect_current_page` tool).
//
// Usage from any page component:
//
//   import { useScreenMeta } from "../chat/ScreenMetaContext.js";
//
//   const meta = useScreenMeta();
//   useEffect(() => meta.register({
//     page_kind: "locations_list",
//     data_summary: () => ({
//       rows_count: state.rows?.length ?? 0,
//       filtered_by: siteFilter ? { site_id: siteFilter } : null,
//     }),
//     drawer_open: editing ? `location_form_${editing.mode}` : null,
//   }), [state, siteFilter, editing]);
//
// The chat bubble reads via `meta.getSnapshot()` when assembling the
// auto-attached Layer C-min screen header (and when the
// inspect_current_page tool runs).
//
// Phase 1 (PR #5) ships the context + a minimal C-min snapshot. Pages
// don't need to opt-in to register — when no page registers, getSnapshot()
// returns just route + tenant info. Phase 2 work (per spec §6.4) will
// add data-tour-id-based rich inspection.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDashboardSession } from "../auth/useDashboardSession.js";

export interface ScreenMetaRegistration {
  /** Stable identifier for the page kind. Examples: 'locations_list',
   *  'site_detail', 'task_assignments'. Used to drive AI's "what page is
   *  user on" reasoning without scraping route paths. */
  page_kind: string;
  /** Optional callback returning a small JSON-able summary of what's
   *  visible. Read fresh each time getSnapshot() runs (not cached). */
  data_summary?: () => unknown;
  /** Open drawer's machine name, e.g. 'location_form_new'. Null/missing
   *  when no drawer is open. */
  drawer_open?: string | null;
  /** ID of the currently selected row (when applicable). */
  selected_row_id?: string | null;
}

/** Minimal header always attached to every user message — route +
 *  tenant + page title + drawer state. ~30-50 tokens, sent in clear. */
export interface ScreenMetaMin {
  route: string;
  query: string;
  page_title: string | null;
  page_kind: string | null;
  tenant_id: string | null;
  super_admin_acting_as_tenant: string | null;
  drawer_open: string | null;
  /** AM-6 — operator's current dashboard mode ("ai" | "professional").
   *  AI uses this to gate propose_tour (disabled in AI mode) + to pick
   *  language about which sidebar / drawer the operator sees. Read
   *  from localStorage so we don't add a hard provider-order dependency
   *  on AiModeContext (this provider must wrap AiModeContext to keep
   *  the existing useScreenMeta surface stable). */
  ai_mode: "ai" | "professional" | null;
}

/** Richer snapshot — emitted only when AI calls `inspect_current_page`
 *  with `include_rows` or `include_form_state`. Phase 1 implements only
 *  the minimal-plus-summary subset; data-tour-id-based form-state
 *  introspection is in PR #6 alongside the tour engine. */
export interface ScreenMetaRich extends ScreenMetaMin {
  data_summary: unknown | null;
  selected_row_id: string | null;
}

interface ScreenMetaCtx {
  register: (reg: ScreenMetaRegistration) => () => void;
  getSnapshotMin: () => ScreenMetaMin;
  getSnapshotRich: () => ScreenMetaRich;
}

const ScreenMetaContext = createContext<ScreenMetaCtx | null>(null);

/** AM-6 — read ai_mode from localStorage instead of useAiMode() because
 *  ScreenMetaProvider wraps AiModeProvider (existing app shell order).
 *  AiModeContext mirrors writes to this same key, so the value is
 *  reliably fresh after the first hydration. Returns null before any
 *  toggle interaction; AI treats null as「assume professional」. */
const AI_MODE_STORAGE_KEY = "location.dashboard.ai_mode";
function readAiModeFromStorage(): "ai" | "professional" | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(AI_MODE_STORAGE_KEY);
    if (raw === "ai" || raw === "professional") return raw;
    return null;
  } catch {
    return null;
  }
}

export function ScreenMetaProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const location = useLocation();
  const session = useDashboardSession();
  const registrationsRef = useRef<ScreenMetaRegistration[]>([]);
  const [, setVersion] = useState(0);

  const register = useCallback((reg: ScreenMetaRegistration): (() => void) => {
    registrationsRef.current.push(reg);
    setVersion((v) => v + 1);
    return () => {
      registrationsRef.current = registrationsRef.current.filter((r) => r !== reg);
      setVersion((v) => v + 1);
    };
  }, []);

  const buildBase = useCallback((): ScreenMetaMin => {
    const reg = registrationsRef.current[registrationsRef.current.length - 1] ?? null;
    // Best-effort page_title from document.title minus our app suffix.
    const title = typeof document !== "undefined"
      ? document.title.replace(/\s*[|—-].*$/, "").trim() || null
      : null;
    return {
      route: location.pathname,
      query: location.search.replace(/^\?/, ""),
      page_title: title,
      page_kind: reg?.page_kind ?? null,
      tenant_id: session.super_admin
        ? (session.acting_tenant_id ?? null)
        : (session.tenant_id ?? null),
      super_admin_acting_as_tenant: session.super_admin
        ? (session.acting_tenant_id ?? null)
        : null,
      drawer_open: reg?.drawer_open ?? null,
      ai_mode: readAiModeFromStorage(),
    };
  }, [location.pathname, location.search, session.super_admin, session.acting_tenant_id, session.tenant_id]);

  const getSnapshotMin = useCallback((): ScreenMetaMin => buildBase(), [buildBase]);
  const getSnapshotRich = useCallback((): ScreenMetaRich => {
    const reg = registrationsRef.current[registrationsRef.current.length - 1] ?? null;
    let summary: unknown | null = null;
    if (reg?.data_summary) {
      try { summary = reg.data_summary(); }
      catch { summary = { _error: "data_summary_threw" }; }
    }
    return {
      ...buildBase(),
      data_summary: summary,
      selected_row_id: reg?.selected_row_id ?? null,
    };
  }, [buildBase]);

  return (
    <ScreenMetaContext.Provider value={{ register, getSnapshotMin, getSnapshotRich }}>
      {children}
    </ScreenMetaContext.Provider>
  );
}

/** Hook for page components to register their per-mount meta + read
 *  the current snapshot. */
export function useScreenMeta(): ScreenMetaCtx {
  const ctx = useContext(ScreenMetaContext);
  if (!ctx) {
    // Outside provider — return a no-op shape so chat bubble still works
    // on pages that haven't been wrapped. Useful during dev / tests.
    return {
      register: () => () => undefined,
      getSnapshotMin: () => ({
        route: typeof window !== "undefined" ? window.location.pathname : "/",
        query: typeof window !== "undefined" ? window.location.search.replace(/^\?/, "") : "",
        page_title: null,
        page_kind: null,
        tenant_id: null,
        super_admin_acting_as_tenant: null,
        drawer_open: null,
        ai_mode: readAiModeFromStorage(),
      }),
      getSnapshotRich: () => ({
        route: typeof window !== "undefined" ? window.location.pathname : "/",
        query: typeof window !== "undefined" ? window.location.search.replace(/^\?/, "") : "",
        page_title: null,
        page_kind: null,
        tenant_id: null,
        super_admin_acting_as_tenant: null,
        drawer_open: null,
        ai_mode: readAiModeFromStorage(),
        data_summary: null,
        selected_row_id: null,
      }),
    };
  }
  return ctx;
}

/** Convenience hook — register once on mount, unregister on unmount.
 *  Pass a callback that returns the registration shape so it can pick
 *  up state from a `useState` / `useMemo` closure without stale data. */
export function useRegisterScreenMeta(build: () => ScreenMetaRegistration, deps: React.DependencyList): void {
  const meta = useScreenMeta();
  useEffect(() => {
    const reg = build();
    return meta.register(reg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
